import { createFileRoute, Link } from '@tanstack/react-router';
import styled from 'styled-components';
import api from '@/api';
import { Badge } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';

export const Route = createFileRoute(
  '/_admin/onboarding-slides/$onboardingId/$slideIndex',
)({
  component: SlideDetail,
});

function SlideDetail() {
  const { onboardingId, slideIndex } = Route.useParams();
  const { data, isLoading, error } = api.getOnboardingSlide.useQuery({
    path: {
      onboarding_id: Number(onboardingId),
      slide_index: Number(slideIndex),
    },
  });

  if (isLoading) return <Message>Loading slide...</Message>;
  if (error) return <Message $error>Error loading slide</Message>;
  if (!data) return <Message $error>Slide not found</Message>;

  const contentHtml = data.content ? (
    // biome-ignore lint/security/noDangerouslySetInnerHtml: admin view of backend-authored slide content
    <ContentPreview dangerouslySetInnerHTML={{ __html: data.content }} />
  ) : (
    <SlideText>—</SlideText>
  );

  return (
    <Container>
      <BackLink>
        <Link
          to="/onboarding-slides"
          search={{ onboarding: Number(onboardingId) }}
        >
          &larr; Back to slides
        </Link>
      </BackLink>

      <PageTitle>Slide {data.slide_index}</PageTitle>

      <Section>
        <SectionTitle>Details</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Slide Index</FieldLabel>
            <FieldValue>{data.slide_index}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Layout</FieldLabel>
            <FieldValue>{data.layout}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Enabled</FieldLabel>
            <FieldValue>
              <Badge $variant={data.enabled ? 'success' : 'error'}>
                {data.enabled ? 'Yes' : 'No'}
              </Badge>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Button Text</FieldLabel>
            <FieldValue>{data.next_step_button_text || '—'}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Title</SectionTitle>
        <SlideText>{data.title || '—'}</SlideText>
      </Section>

      <Section>
        <SectionTitle>Content</SectionTitle>
        {contentHtml}
      </Section>

      {data.xprofile_fields.length > 0 && (
        <Section>
          <SectionTitle>XProfile Fields</SectionTitle>
          <FieldList>
            {data.xprofile_fields.map((fieldId) => (
              <FieldChip key={fieldId}>Field #{fieldId}</FieldChip>
            ))}
          </FieldList>
        </Section>
      )}
    </Container>
  );
}

const Container = styled.div``;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;

const SlideText = styled.p`
  margin: var(--component-margin);
  color: var(--grey-900);
`;

const ContentPreview = styled.div`
  margin: var(--component-margin);
  padding: 1rem;
  background: var(--grey-25);
  border: 1px solid var(--grey-200);
  border-radius: 4px;
  font-size: 0.9375rem;
  line-height: 1.6;

  p {
    margin: 0 0 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const FieldList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: var(--component-margin);
`;

const FieldChip = styled.span`
  background: var(--grey-100);
  border: 1px solid var(--grey-300);
  border-radius: 4px;
  color: var(--grey-700);
  font-size: 0.8125rem;
  padding: 0.25rem 0.5rem;
`;
